import React, { Component } from "react";
import { Formik } from "formik";
import NoticeBody from "./NoticeBody";
import { TableInput, ValueView, tableDef } from "./TableInput";
import {
  fieldName, orderedQuestions, initialValues, buildSchema, buildPayload, RATING_VALUES, SHORT_MAX, LONG_MAX,
  employeeQuestions, hrQuestions, currentAssignment, isPerPerson, AGREE_REQUIRED,
} from "./formBodyHelpers";

/**
 * The body of a `form` item inside BroadcastModal: one input per question (SPEC 7.2), Yup
 * validation (7.3) and the POST /responses payload (19.4).
 *
 *   <FormBody ref item submitResponse />
 *
 * The body owns no button. The frame's Submit calls ref.submit(), which resolves true once the
 * server accepted the answers and false otherwise (validation failed, server said no, network
 * failed, or a submission is already in flight). Validation messages sit under their question;
 * the server's messages go in a role="alert" block above the questions.
 *
 * SPEC 23.5 / 23.6 / 23.8: a per-person form (item.per_person) shows the content, the HR values of
 * the current assignment read-only (tables as tables), then the employee questions only. With
 * item.require_signature (any form) an "I, {signerName}, agree to the above" box sits under the
 * questions and must be ticked before anything is sent. `this.result` keeps the server's content
 * of the accepted submit (signed_at, pdf_url, remaining) for the queue.
 */

export const GENERIC_ERROR = "Your answers could not be submitted. Please try again.";

const Help = ({ text }) => (text ? <small className="form-text text-muted">{text}</small> : null);
const Problem = ({ message }) => (message ? <div className="text-danger broadcast-question-error">{message}</div> : null);

class FormBody extends Component {
  constructor(props) {
    super(props);
    const all = orderedQuestions(props.item);
    this.questions = employeeQuestions(props.item, all);
    this.hrQuestions = hrQuestions(props.item, all);
    this.schema = buildSchema(this.questions);
    this.result = null;
    this.state = {
      agree: false,       // the signature box (require_signature)
      attempted: false,   // show per-question messages only after the first submit
      submitting: false,  // inputs disabled, a second submit() is refused
      serverErrors: [],   // the alert block
    };
    this.inFlight = false;
    this.formik = null;
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  // Called by the frame's Submit button. Resolves true only when the answers were accepted.
  signs = () => !!this.props.item.require_signature;
  // A per-person or signed form can be sent with no employee question at all.
  answersOnly = () => !isPerPerson(this.props.item) && !this.signs();

  submit = () => {
    if (this.inFlight) return Promise.resolve(false);
    if (this.questions.length === 0 && this.answersOnly()) return Promise.resolve(false);
    if (this.questions.length > 0 && !this.formik) return Promise.resolve(false);
    this.setState({ attempted: true });
    const formik = this.formik;
    const validate = this.questions.length > 0 ? formik.validateForm() : Promise.resolve({});
    return validate.then((errors) => {
      if (Object.keys(errors || {}).length > 0) return false;
      if (this.signs() && !this.state.agree) return false;
      const values = this.questions.length > 0 ? formik.values : {};
      return this.send(buildPayload(this.props.item, this.questions, values, this.state.agree));
    });
  };

  send = (payload) => {
    this.inFlight = true;
    this.setState({ submitting: true, serverErrors: [] });
    return Promise.resolve()
      .then(() => this.props.submitResponse(payload))
      .then(
        (result) => {
          const ok = !!(result && result.ok);
          if (ok) this.result = (result && result.content) || {};
          this.finish(ok ? [] : (result && result.errors && result.errors.length ? result.errors : [GENERIC_ERROR]));
          return ok;
        },
        () => {
          this.finish([GENERIC_ERROR]);
          return false;
        }
      );
  };

  finish = (serverErrors) => {
    this.inFlight = false;
    if (this.mounted) this.setState({ submitting: false, serverErrors: serverErrors });
  };

  // ---- inputs

  renderInput = (q, name, value, setFieldValue) => {
    const disabled = this.state.submitting;
    const id = "bq-" + q.id;
    const set = (v) => setFieldValue(name, v);
    switch (q.qtype) {
      case "long_text":
        return <textarea className="form-control" id={id} maxLength={LONG_MAX} rows={4} value={value || ""} disabled={disabled}
          onChange={(e) => set(e.target.value)} />;
      case "rating":
        return (
          <div className="broadcast-rating">
            {RATING_VALUES.map((n) => (
              <div className="form-check form-check-inline" key={n}>
                <input type="radio" className="form-check-input" id={id + "-r" + n} name={name} value={n}
                  checked={value === n} disabled={disabled} onChange={() => set(n)} />
                <label className="form-check-label" htmlFor={id + "-r" + n}>{n}</label>
              </div>
            ))}
          </div>
        );
      case "single":
        return (q.options || []).map((option, i) => (
          <div className="form-check" key={i}>
            <input type="radio" className="form-check-input" id={id + "-o" + i} name={name} value={option}
              checked={value === option} disabled={disabled} onChange={() => set(option)} />
            <label className="form-check-label" htmlFor={id + "-o" + i}>{option}</label>
          </div>
        ));
      case "multi": {
        const picked = Array.isArray(value) ? value : [];
        // Kept in option order so the server receives a stable array whatever the click order.
        const toggle = (option) => set((q.options || []).filter((o) => (o === option ? !picked.includes(o) : picked.includes(o))));
        return (q.options || []).map((option, i) => (
          <div className="form-check" key={i}>
            <input type="checkbox" className="form-check-input" id={id + "-o" + i} value={option}
              checked={picked.includes(option)} disabled={disabled} onChange={() => toggle(option)} />
            <label className="form-check-label" htmlFor={id + "-o" + i}>{option}</label>
          </div>
        ));
      }
      case "yes_no":
        return (
          <div className="btn-group broadcast-yes-no" role="group">
            {[["Yes", 1], ["No", 0]].map(([text, n]) => (
              <button type="button" key={text} className={"btn " + (value === n ? "btn-primary" : "btn-outline-primary")}
                aria-pressed={value === n ? "true" : "false"} disabled={disabled} onClick={() => set(n)}>
                {text}
              </button>
            ))}
          </div>
        );
      case "table": {
        const def = tableDef(q);
        return <TableInput id={id} label={q.label} columns={def.columns} maxRows={def.maxRows} value={value} disabled={disabled} onChange={set} />;
      }
      case "date":
        return <input type="date" className="form-control" id={id} value={value || ""} disabled={disabled}
          onChange={(e) => set(e.target.value)} />;
      default:
        return <input type="text" className="form-control" id={id} maxLength={SHORT_MAX} value={value || ""} disabled={disabled}
          onChange={(e) => set(e.target.value)} />;
    }
  };

  // Text-like questions label their input (htmlFor); the pick types label their own options.
  renderQuestion = (q, formik) => {
    const name = fieldName(q);
    const labelled = ["short_text", "long_text", "date"].includes(q.qtype);
    const message = this.state.attempted ? formik.errors[name] : null;
    return (
      <div className="form-group broadcast-question" key={q.id}>
        <label htmlFor={labelled ? "bq-" + q.id : undefined} id={"bq-label-" + q.id}>{q.label}</label>
        {q.required ? <span className="broadcast-required" title="Required">*</span> : null}
        <Help text={q.help_text} />
        {this.renderInput(q, name, formik.values[name], formik.setFieldValue)}
        <Problem message={message} />
      </div>
    );
  };

  // The HR-filled values of the assignment on screen, read-only (hr_values keyed by question id string).
  renderHrValues = () => {
    if (this.hrQuestions.length === 0) return null;
    const assignment = currentAssignment(this.props.item);
    const values = (assignment && assignment.hr_values) || {};
    return (
      <div className="broadcast-hr-values" data-testid="broadcast-hr-values">
        {this.hrQuestions.map((q) => (
          <div className="form-group broadcast-hr-value" key={q.id}>
            <div className="broadcast-hr-value-label"><strong>{q.label}</strong></div>
            <ValueView question={q} value={values[String(q.id)]} />
          </div>
        ))}
      </div>
    );
  };

  renderAgree = () => {
    if (!this.signs()) return null;
    const name = String(this.props.signerName || "").trim();
    const label = name ? "I, " + name + ", agree to the above" : "I agree to the above";
    return (
      <div className="form-group broadcast-agree">
        <div className="form-check">
          <input type="checkbox" className="form-check-input" id="broadcast-agree" checked={this.state.agree}
            disabled={this.state.submitting} onChange={(e) => this.setState({ agree: e.target.checked })} />
          <label className="form-check-label" htmlFor="broadcast-agree">{label}</label>
        </div>
        <Problem message={this.state.attempted && !this.state.agree ? AGREE_REQUIRED : null} />
      </div>
    );
  };

  render() {
    const perPerson = isPerPerson(this.props.item);
    if (this.questions.length === 0 && this.answersOnly()) {
      return <div className="broadcast-form-body"><p>This form has no questions</p></div>;
    }
    const { serverErrors } = this.state;
    return (
      <div className="broadcast-form-body">
        {serverErrors.length ? (
          <div role="alert" className="alert alert-danger">
            <ul className="mb-0">{serverErrors.map((m, i) => <li key={i}>{m}</li>)}</ul>
          </div>
        ) : null}
        {perPerson && this.props.item.content_html ? <NoticeBody item={this.props.item} /> : null}
        {perPerson ? this.renderHrValues() : null}
        {this.questions.length > 0 ? (
          <Formik initialValues={initialValues(this.questions)} validationSchema={this.schema} onSubmit={() => {}}>
            {(formik) => {
              this.formik = formik;
              return (
                <form onSubmit={(e) => e.preventDefault()} noValidate>
                  {this.questions.map((q) => this.renderQuestion(q, formik))}
                </form>
              );
            }}
          </Formik>
        ) : null}
        {this.renderAgree()}
      </div>
    );
  }
}

export default FormBody;
